import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { catchError, map, Observable, of } from 'rxjs';
import { AuthenticationService } from 'src/app/services/authentication.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class TeamDynamicService {
  private baseUrl = environment.baseUrl + '/';

  constructor(private http: HttpClient, private auth: AuthenticationService) { }

  getOption(teamId?: string) {
    const workspaceId = localStorage.getItem('WORKSPACE_UUID');
    let headers = new HttpHeaders();

    if (workspaceId) {
      headers = headers.set('X-Workspace', workspaceId);
    }

    if (teamId) {
      headers = headers.set('X-Team', teamId);
    }

    return { headers };
  }

  getIndicatorInterval(): Observable<any> {
    const options = this.getOption();
    return this.http.get<any>(this.baseUrl + 'indicator-intervals', options)
      .pipe(
        map(res => {
            return res._embedded.indicatorIntervals;
          },
          (err: any) => {
            return err;
          }
        )
      );
  }

  getWorkspaceTeamDynamic(startDate?: any, endDate?: any): Observable<any> {
      const options = this.getOption();
      let params = new HttpParams();

      if (startDate && endDate) {
        params = params
          .set('startDate', startDate)
          .set('endDate', endDate);
      }
      return this.http.get<any>(this.baseUrl + 'workspace-team-dynamic', {...options, ...(params && {params})})
          .pipe(
              map(res => res),
              catchError((err: any) => {
                return of({error: err});
              })
            );
  }

  getTeamsTeamDynamic(filterOptions?: { startDate?: string; endDate?: string;
                                       timedQuestionCollectionUuid?: string | null; sort?: string }): Observable<any> {
    const options = this.getOption();
    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions.startDate)
        .set('endDate', filterOptions.endDate);
    }

    if (filterOptions?.timedQuestionCollectionUuid) {
      params = params.set('timedQuestionCollection', filterOptions.timedQuestionCollectionUuid);
    }

    if (filterOptions?.sort) {
      params = params.set('sort', filterOptions.sort);
    }
    return this.http.get<any>(this.baseUrl + 'team-team-dynamics', {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              return of({error: err});
            })
          );
  }

  getActiveWorkspaceTeamDynamics(): Observable<any> {
    const options = this.getOption();

    return this.http.get<any>(this.baseUrl + 'active-workspace-team-dynamics', options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              return of({error: err});
            })
          );
  }

  getTeamDynamicDevelopmentScores(filterOptions?: { timedQuestionCollectionUuid?: string; startDate?: string; endDate?: string}): Observable<any> {
    const options = this.getOption();
    let url = 'team-dynamic-scores';

    if (filterOptions?.timedQuestionCollectionUuid) {
      url += `/timedQuestionCollectionUuid/${filterOptions.timedQuestionCollectionUuid}`;
    }

    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions?.startDate)
        .set('endDate', filterOptions?.endDate);
    }
    return this.http.get<any>(this.baseUrl + url, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              return of({error: err});
            })
          );
  }

  getWorkspaceTeamDynamicDetail(collectionUuid: string, startDate?: any, endDate?: any): Observable<any> {
    const options = this.getOption();
    let params = new HttpParams();

    if (startDate && endDate) {
      params = params
        .set('startDate', startDate)
        .set('endDate', endDate);
    }
    return this.http.get<any>(this.baseUrl + 'workspace-team-dynamic-detail/timedQuestionCollectionUuid/'+ collectionUuid, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              return of({error: err});
            })
          );
  }

  patchWorkspaceTeamDynamicVisibility(uuid: string, teamVisibility: boolean): Observable<any> {
    const options = this.getOption();

    return this.http.patch<any>(this.baseUrl + 'workspace-team-dynamic-visibility/' + uuid,
      {teamVisibility}, options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              return of({error: err});
            })
          );
  }

  getQuestionWorkspaceTeamDynamic(collectionUuid: string,
                                  filterOptions?: { sort?: string | null; startDate?: string; endDate?: string }): Observable<any> {
    const options = this.getOption();
    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions.startDate)
        .set('endDate', filterOptions.endDate);
    }

    if (filterOptions?.sort) {
      params = params.set('sort', filterOptions.sort);
    }
    return this.http.get<any>(this.baseUrl + 'question-workspace-team-dynamic/timedQuestionCollectionUuid/'+ collectionUuid, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              return of({error: err});
            })
          );
  }

  getStatementScaledResponses(questionUuid: string, collectionUuid?: string | null,
                              teamUuid?: string | null): Observable<any> {
    const options = this.getOption();
    const body: any = collectionUuid ? {timedQuestionCollection: collectionUuid} : {};

    if (teamUuid) {
      body.teamUuid = teamUuid;
    }

    return this.http.post<any>(this.baseUrl + 'team-dynamic-scaled-question-responses/questionUuid/' + questionUuid, body, options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              return of({error: err});
            })
          );
  }

  getStatementOpenResponses(questionUuid: string, page: number, limit: number,
                            teamUuid?: string | null): Observable<any> {
    const options = this.getOption();
    const body = teamUuid ? {teamUuid} : {};

    return this.http.post<any>(this.baseUrl + 'team-dynamic-open-question-responses/questionUuid/' + questionUuid +
      '?page=' + page + '&limit=' + limit, body, options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-dynamic-open-question-responses: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getTeamReportTeamDynamic(teamUuid: string, filterOptions?: { startDate?: string; endDate?: string }): Observable<any> {
    const options = this.getOption(teamUuid);
    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions.startDate)
        .set('endDate', filterOptions.endDate);
    }
    return this.http.get<any>(this.baseUrl + 'team-team-dynamic', {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-team-dynamic: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getTeamTeamDynamicScores(teamUuid: string,
                           filterOptions?: { timedQuestionCollectionUuid?: string; startDate?: string;
                                             endDate?: string }): Observable<any> {
    const options = this.getOption(teamUuid);
    let url = 'team-team-dynamic-scores';

    if (filterOptions?.timedQuestionCollectionUuid) {
      url += `/timedQuestionCollectionUuid/${filterOptions.timedQuestionCollectionUuid}`;
    }

    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions?.startDate)
        .set('endDate', filterOptions?.endDate);
    }
    return this.http.get<any>(this.baseUrl + url, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-team-dynamic-scores: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getTeamMembers(uuid: string): Observable<any> {
    const options = this.getOption();
    return this.http.get<any>(this.baseUrl + 'team/members/teamUuid/' + uuid + '?activeOnly=true', options)
      .pipe(
        map(res => {
            return res._embedded.members;
          }),
        catchError((err: any) => {
            console.log('team-members: '+JSON.stringify(err));
            return of({error: err});
        })
      );
  }

  getQuestionTeamTeamDynamic(teamUuid: string,
                           filterOptions?: { timedQuestionCollectionUuid?: string; sort?: string | null;
                                             startDate?: string; endDate?: string }): Observable<any> {
    const options = this.getOption(teamUuid);
    let url = 'question-team-team-dynamic';

    if (filterOptions?.timedQuestionCollectionUuid) {
      url += `/timedQuestionCollectionUuid/${filterOptions.timedQuestionCollectionUuid}`;
    }

    let params = new HttpParams();

    if (filterOptions?.startDate && filterOptions?.endDate) {
      params = params
        .set('startDate', filterOptions?.startDate)
        .set('endDate', filterOptions?.endDate);
    }

    if (filterOptions?.sort) {
      params = params.set('sort', filterOptions.sort);
    }
    return this.http.get<any>(this.baseUrl + url, {...options, ...(params && {params})})
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('question-team-team-dynamic: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getTeamTeamDynamicsHighlights(teamUuid: string): Observable<any> {
    const options = this.getOption(teamUuid);

    return this.http.get<any>(this.baseUrl + 'team-dynamic-highlights', options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-dynamic-highlights: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  getWorkspaceTeamDynamicsHighlights(): Observable<any> {
    const options = this.getOption();

    return this.http.get<any>(this.baseUrl + 'workspace-team-dynamic-highlights', options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('workspace-team-dynamic-highlights: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }

  patchTeamDynamicHighlightStatus(uuid: string, status: string, teamUuid?: string): Observable<any> {
    const options = this.getOption(teamUuid);

    return this.http.patch<any>(this.baseUrl + 'team-dynamic-highlight/' + uuid, {status}, options)
        .pipe(
            map(res => res),
            catchError((err: any) => {
              console.log('team-dynamic-highlight: '+JSON.stringify(err));
              return of({error: err});
            })
          );
  }
}
